import React, { useEffect, useRef, useState } from "react";
import "./TextEdit.scss";
import DOMPurify from "dompurify";

const TextEdit = (props) => {

    const { value, setValue, type, placeholder, edit, onChange, variables, focusVariable, setFocusVariable, className, setSelected } = props;
    
    const [ localValue, setLocalValue ] = useState(value);

    const [ editing, setEditing ] = useState(edit);

    const valueRef = useRef(value);
    const inputRef = useRef(null);

    useEffect(() => {
        valueRef.current = localValue;
    }, [localValue]);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {

        const ref = inputRef.current;

        if (ref && editing) {
            setTimeout(() => {
                ref?.focus();
            }, 500);
        }

        function handleClickOutside(event) {
            if (ref && !ref.contains(event.target)) {
                setValue(valueRef.current);
                setEditing(false);
                if(event.target.className !== "redraft" && event.target.className !== "delete" && setFocusVariable !== undefined)
                    setFocusVariable(null);
                if(event.target.className !== "redraft" && event.target.className !== "delete" && setSelected !== undefined)
                    setSelected(false);
            }
        }

        function handleKeyPress(event) {
            if (event.key === 'Enter' || event.keyCode === 13) {
                setEditing(false);
                setValue(valueRef.current);
                if(setFocusVariable !== undefined)
                    setFocusVariable(null);
                if(setSelected !== undefined)
                    setSelected(false);
            }
        }

        if (editing) {
            document.addEventListener('mousedown', handleClickOutside);
            ref.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (ref) {
                ref.removeEventListener('keydown', handleKeyPress);
            }
        };
    }, [editing, setFocusVariable, setSelected, setValue]);

    const contentRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {

            let target = e.target;
            while (target != null && target.dataset && !target.dataset.clickable) {
                target = target.parentElement;
            }

            if (target && target.dataset && target.dataset.variableKey) {
                setFocusVariable(target.dataset.variableKey);
            }
        };

        const currentContentRef = contentRef.current;
        if (currentContentRef) {
            currentContentRef.addEventListener('click', handleClick);
        }

        return () => {
            if (currentContentRef) {
                currentContentRef.removeEventListener('click', handleClick);
            }
        };
    }, [setFocusVariable]);

    useEffect(() => {
        let originalContent = localValue;
        let needsUpdate = false;

        if (variables) {
            variables.forEach(variableGroup => {
                variableGroup.variables.forEach(variable => {
                    const variableRegex = new RegExp(`\\|${variable.key} value="(.*?)"\\|`, 'g');
                    originalContent = originalContent.replace(variableRegex, (match, value) => {
                        if(variable.value !== value) {
                            needsUpdate = true;
                        }
                        return `|${variable.key} value="${variable.value}"|`;
                    });
                });
            });
        }

        if(needsUpdate) setValue(originalContent);

    }, [variables, setValue, localValue]);
    
    function renderContent() {
        let htmlContent = localValue;

        if(htmlContent === "" && placeholder !== undefined) htmlContent = placeholder;
    
        if (variables) {
            variables.forEach(variableGroup => {
                variableGroup.variables.forEach(variable => {
                    const variableRegex = new RegExp(`\\|${variable.key} value="(.*?)"\\|`, 'g');
                    htmlContent = htmlContent.replace(variableRegex, (match, value) => {
                        const className = `variable ${value === "" ? "empty" : ""} ${focusVariable === variable.key ? "focus" : ""} ${editing ? "editing" : ""}`;
                        const displayValue = value !== "" ? value : variable.name;
                        return `<div class="${className}" data-variable-key="${variable.key}" data-clickable="true">${displayValue}</div>`;
                    });
                });
            });
        
            htmlContent = htmlContent.replace(/\n/g, '<br />');
        }
    
        return { __html: DOMPurify.sanitize(htmlContent) };
    }

    const handleValueChange = (e) => {
        const newValue = e.target.value;
        if(onChange !== undefined)
            onChange(e.target.value);
        setLocalValue(newValue);
    };

    return (
        <div className={"text-edit " + (localValue === "" ? "empty " : "") + (className ? className : "")} onClick={(event) => {event.stopPropagation(); if(setSelected !== undefined) {setSelected(true);}}}>
            { editing && 
                ( type === "textarea" ? 
                <textarea ref={inputRef} value={localValue} onChange={handleValueChange} placeholder={placeholder} /> :
                <input ref={inputRef} value={localValue} onChange={handleValueChange} placeholder={placeholder} /> )
            }
            <span ref={contentRef} className={editing ? "hideValue" : "showValue"} onClick={() => setEditing(true)} dangerouslySetInnerHTML={renderContent()} />
        </div>
    );
};

export default TextEdit;